import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  listPatients,
  listBillingProviders,
} from '../../utils/approvalsApi';
import ProviderRosterPicker from './components/ProviderRosterPicker';

const PAGE_SX = { bgcolor: '#fff', minHeight: '100vh' };
const CONTAINER_SX = { py: 4 };
const BREADCRUMB_SX = { mb: 2 };

const HEADER_SX = { mb: 3 };
const TITLE_SX = { fontSize: 28, fontWeight: 700, letterSpacing: '-0.01em' };
const SUBTITLE_SX = { fontSize: 14, color: '#64748B', mt: 0.5 };

const STEP_INDICATOR_SX = { fontSize: 13, color: '#64748B', mb: 2 };

const SECTION_HEADER_SX = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1.5,
  mb: 2,
};
const SECTION_NUM_SX = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 12,
  color: '#64748B',
};
const SECTION_TITLE_SX = { fontSize: 20, fontWeight: 700 };

const CARD_LIST_SX = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 2,
};

const sourceCardSx = (selected) => ({
  flex: 1,
  border: '1px solid #000',
  borderLeft: selected ? '4px solid #000' : '1px solid #000',
  p: 2,
  cursor: 'pointer',
  bgcolor: '#fff',
  '&:hover': { bgcolor: '#F8F9FC' },
});

const SOURCE_LABEL_SX = (selected) => ({
  fontSize: 14,
  fontWeight: selected ? 700 : 600,
});
const SOURCE_SUB_SX = { fontSize: 12, color: '#64748B', mt: 0.5 };

const PATIENT_LIST_SX = {
  border: '1px solid rgba(0,0,0,0.12)',
  mt: 2,
  maxHeight: 360,
  overflowY: 'auto',
};

const PATIENT_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 1.25,
  cursor: 'pointer',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  '&:hover': { bgcolor: '#F8F9FC' },
};

const PATIENT_AVATAR_SX = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  bgcolor: '#000',
  color: '#fff',
  fontWeight: 700,
  fontSize: 13,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

const PATIENT_NAME_SX = { fontWeight: 700, fontSize: 14 };
const MRN_SX = { fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#64748B' };
const DOB_SX = { fontSize: 12, color: '#64748B' };

const EMPTY_HINT_SX = { color: '#64748B', fontSize: 13, mt: 2 };

const PROVIDER_CARD_SX = {
  border: '1px solid #000',
  p: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  bgcolor: '#fff',
};

const PROVIDER_AVATAR_SX = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  bgcolor: '#000',
  color: '#fff',
  fontWeight: 700,
  fontSize: 16,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

const PROVIDER_NAME_SX = { fontWeight: 700, fontSize: 16 };
const PROVIDER_NPI_SX = { fontSize: 13, color: '#64748B', mt: 0.25 };

const RAIL_SX = {
  position: 'sticky',
  top: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const RAIL_CARD_SX = {
  border: '1px solid #000',
  p: 2,
  bgcolor: '#fff',
};

const RAIL_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#64748B',
  mb: 1.5,
};

const META_ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1.5,
  py: 0.75,
  borderBottom: '1px solid rgba(0,0,0,0.06)',
};
const META_ROW_LAST_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1.5,
  py: 0.75,
};
const META_LABEL_SX = { fontSize: 12, color: '#64748B' };
const META_VALUE_SX = {
  fontSize: 12,
  fontWeight: 700,
  color: '#000',
  textAlign: 'right',
  maxWidth: '65%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CONTINUE_BTN_SX = {
  bgcolor: '#000',
  color: '#fff',
  '&:hover': { bgcolor: '#1A1A1A' },
  '&.Mui-disabled': { bgcolor: '#A0ABBB', color: '#fff' },
  fontWeight: 700,
  textTransform: 'none',
};

const STEP_NAV_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 3,
};

const BACK_BTN_SX = {
  color: '#000',
  textTransform: 'none',
};

const SOURCE_OPTIONS = [
  { key: 'claim', label: 'Existing Claim', subtitle: 'Pre-fills payer + DOS' },
  { key: 'appeal', label: 'Existing Appeal', subtitle: 'Attaches to an appeal in progress' },
  { key: 'patient_only', label: 'Patient only', subtitle: 'Pre-emptive signature, no claim yet' },
];

const SOURCE_LABEL_BY_KEY = {
  claim: 'Existing Claim',
  appeal: 'Existing Appeal',
  patient_only: 'Patient only',
};

const fullName = (entity) => {
  if (!entity) return '';
  const first = entity.first_name || '';
  const last = entity.last_name || '';
  const name = `${first} ${last}`.trim();
  return name;
};

const initials = (entity) => {
  if (!entity) return '?';
  const f = (entity.first_name || '').trim()[0] || '';
  const l = (entity.last_name || '').trim()[0] || '';
  const both = `${f}${l}`.toUpperCase();
  if (both) return both;
  return (entity.organization_name || entity.name || '?').trim()[0]?.toUpperCase() || '?';
};

const formatDob = (raw) => {
  if (!raw) return '';
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  return `${m[2]}/${m[3]}/${m[1]}`;
};

const NewApprovalPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [sourceType, setSourceType] = useState('');
  const [patient, setPatient] = useState(null);
  const [provider, setProvider] = useState(null);

  // Step 2 — patient search
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  // Step 4 — provider roster
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerError, setProviderError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const q = patientQuery.trim();
    if (q.length < 2) {
      setPatientResults([]);
      setPatientsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPatientsLoading(true);
    const timer = window.setTimeout(() => {
      listPatients({ search: q })
        .then((res) => {
          if (cancelled) return;
          const data = res?.data;
          const list = Array.isArray(data) ? data : (data?.data || data?.results || []);
          setPatientResults(list);
        })
        .catch(() => {
          if (!cancelled) setPatientResults([]);
        })
        .finally(() => {
          if (!cancelled) setPatientsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [patientQuery]);

  useEffect(() => {
    if (step !== 4) return undefined;
    if (provider) return undefined;
    let cancelled = false;
    setProviderLoading(true);
    setProviderError(false);
    listBillingProviders()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.results || []);
        if (list.length === 0) {
          setProvider(null);
        } else {
          const defaultProv = list.find((p) => p.is_default) || list[0];
          setProvider(defaultProv);
        }
      })
      .catch(() => {
        if (!cancelled) setProviderError(true);
      })
      .finally(() => {
        if (!cancelled) setProviderLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, provider]);

  const canContinue = !!patient && !!provider;

  const handleSelectSource = (key) => {
    setSourceType(key);
    setStep(2);
  };

  const handleSelectPatient = (p) => {
    setPatient(p);
    if (sourceType === 'patient_only') {
      setStep(4);
    } else {
      setStep(3);
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;
    const binding = {
      patient: {
        id: patient.patient_id || patient.id,
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        mrn: patient.mrn || '',
        date_of_birth: patient.date_of_birth || '',
      },
      provider: {
        id: provider.id,
        npi: provider.npi || '',
        first_name: provider.first_name || '',
        last_name: provider.last_name || '',
        name: fullName(provider) || provider.organization_name || '',
        taxonomy_code: provider.taxonomy_code || '',
      },
      sourceType,
    };
    navigate('/approvals/compose', { state: { binding } });
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      return;
    }
    if (step === 3) {
      setStep(2);
      return;
    }
    if (step === 4) {
      if (sourceType === 'patient_only') {
        setStep(2);
      } else {
        setStep(3);
      }
    }
  };

  const sourceLabel = useMemo(
    () => (sourceType ? SOURCE_LABEL_BY_KEY[sourceType] : '—'),
    [sourceType],
  );

  return (
    <Box sx={PAGE_SX}>
      <Container maxWidth="lg" sx={CONTAINER_SX}>
        <Breadcrumbs sx={BREADCRUMB_SX} aria-label="breadcrumb">
          <MuiLink component={RouterLink} to="/approvals" underline="hover" color="inherit">
            Approvals
          </MuiLink>
          <Typography color="text.primary">New approval</Typography>
        </Breadcrumbs>

        <Box sx={HEADER_SX}>
          <Typography sx={TITLE_SX}>New approval</Typography>
          <Typography sx={SUBTITLE_SX}>
            Bind this request to a patient and signing provider before composing.
          </Typography>
        </Box>

        <Typography sx={STEP_INDICATOR_SX}>Step {step} of 4</Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {step === 1 && (
              <Box>
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_NUM_SX}>01</Typography>
                  <Typography sx={SECTION_TITLE_SX}>Start from</Typography>
                </Box>
                <Box sx={CARD_LIST_SX}>
                  {SOURCE_OPTIONS.map((opt) => {
                    const selected = sourceType === opt.key;
                    return (
                      <Box
                        key={opt.key}
                        sx={sourceCardSx(selected)}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectSource(opt.key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectSource(opt.key);
                          }
                        }}
                      >
                        <Typography sx={SOURCE_LABEL_SX(selected)}>{opt.label}</Typography>
                        <Typography sx={SOURCE_SUB_SX}>{opt.subtitle}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {step === 2 && (
              <Box>
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_NUM_SX}>02</Typography>
                  <Typography sx={SECTION_TITLE_SX}>Patient</Typography>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search patients by name"
                  value={patientQuery}
                  onChange={(e) => setPatientQuery(e.target.value)}
                  autoFocus
                  InputProps={{
                    endAdornment: patientsLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} aria-label="Searching patients" />
                      </InputAdornment>
                    ) : null,
                  }}
                />
                {patientQuery.trim().length < 2 ? (
                  <Typography sx={EMPTY_HINT_SX}>
                    Type at least 2 characters to search.
                  </Typography>
                ) : patientResults.length === 0 && !patientsLoading ? (
                  <Typography sx={EMPTY_HINT_SX}>
                    No patients match that search.
                  </Typography>
                ) : (
                  <Box sx={PATIENT_LIST_SX}>
                    {patientResults.map((p) => (
                      <Box
                        key={p.id}
                        sx={PATIENT_ROW_SX}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectPatient(p)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectPatient(p);
                          }
                        }}
                      >
                        <Box sx={PATIENT_AVATAR_SX}>{initials(p)}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={PATIENT_NAME_SX} noWrap>
                            {fullName(p) || '—'}
                          </Typography>
                          <Stack direction="row" spacing={1.5}>
                            <Typography sx={MRN_SX}>MRN {p.mrn || '—'}</Typography>
                            <Typography sx={DOB_SX}>{formatDob(p.date_of_birth)}</Typography>
                          </Stack>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {step === 3 && (
              <Box>
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_NUM_SX}>03</Typography>
                  <Typography sx={SECTION_TITLE_SX}>Source detail</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: '#64748B' }}>
                  Source binding for claims and appeals is coming soon. Click Continue
                  to proceed to provider selection.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => setStep(4)}
                    sx={CONTINUE_BTN_SX}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            )}

            {step === 4 && (
              <Box>
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_NUM_SX}>04</Typography>
                  <Typography sx={SECTION_TITLE_SX}>Signing provider</Typography>
                </Box>
                {providerLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : providerError ? (
                  <Typography sx={EMPTY_HINT_SX}>
                    Could not load billing providers. Try again later.
                  </Typography>
                ) : !provider ? (
                  <Typography sx={EMPTY_HINT_SX}>
                    No providers found. Add a billing provider in Settings.
                  </Typography>
                ) : (
                  <Box sx={PROVIDER_CARD_SX}>
                    <Box sx={PROVIDER_AVATAR_SX}>{initials(provider)}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={PROVIDER_NAME_SX} noWrap>
                        {fullName(provider) || provider.organization_name || '—'}
                      </Typography>
                      <Typography sx={PROVIDER_NPI_SX} noWrap>
                        NPI {provider.npi || '—'}
                        {provider.taxonomy_code ? ` · ${provider.taxonomy_code}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setPickerOpen(true)}
                      sx={BACK_BTN_SX}
                    >
                      Change provider
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={STEP_NAV_SX}>
              {step > 1 ? (
                <Button onClick={handleBack} sx={BACK_BTN_SX}>
                  Back
                </Button>
              ) : (
                <Box />
              )}
              {step === 4 && (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleContinue}
                  disabled={!canContinue}
                  sx={CONTINUE_BTN_SX}
                >
                  Continue
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={RAIL_SX}>
              <Box sx={RAIL_CARD_SX}>
                <Typography sx={RAIL_LABEL_SX}>THIS REQUEST</Typography>
                <Box sx={META_ROW_SX}>
                  <Typography sx={META_LABEL_SX}>Patient</Typography>
                  <Typography sx={META_VALUE_SX}>
                    {patient ? (fullName(patient) || '—') : '—'}
                  </Typography>
                </Box>
                <Box sx={META_ROW_SX}>
                  <Typography sx={META_LABEL_SX}>Provider</Typography>
                  <Typography sx={META_VALUE_SX}>
                    {provider ? (fullName(provider) || provider.organization_name || '—') : '—'}
                  </Typography>
                </Box>
                <Box sx={META_ROW_LAST_SX}>
                  <Typography sx={META_LABEL_SX}>Source</Typography>
                  <Typography sx={META_VALUE_SX}>{sourceLabel}</Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                disableElevation
                onClick={handleContinue}
                disabled={!canContinue}
                sx={CONTINUE_BTN_SX}
                fullWidth
              >
                Continue
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <ProviderRosterPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={setProvider}
        currentId={provider?.id}
      />
    </Box>
  );
};

export default NewApprovalPage;
